import React from 'react';
import styled from 'styled-components';
import { JourneyStatus } from '@dev/ee-api/ee-api-models';
import { FilterIcon, FiltersPopover, SearchInput, palette } from '@dev/ee-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { parse } from 'query-string';
import { omit } from 'lodash-es';
import { useLocation } from 'react-router-dom';

import dayjs from '@/services/tools/dayjs';
import { debounce } from '@/services/tools/debounce';
import { useDateDropdownValue } from '@/services/hooks/useDateDropdownValue';
import { GetAllAutomationsStart, getAutomationStore } from '@/store/automations/actions';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import useDateDropdownOptions from '@/services/hooks/useDateDropdownOptions';
import { MobileFilterTabsProps } from '../../../AutomationsInterface';

const Header = styled.div`
    display: flex;
    margin-top: 32px;
    margin-bottom: 20px;
    justify-content: space-between;
`;
const SearchContainer = styled.div`
    width: 100%;
    & > div {
        border: 1px solid ${palette.gray[3]};
    }
`;
const FiltersContainer = styled.div`
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
    margin-left: 8px;
    padding: 11px 11px;
`;

const RenderSearchInput = (): JSX.Element => {
    const { t } = useTranslation();
    const automations = useSelector(getAutomationStore);
    const location = useLocation();
    const queryParams = parse(location.search);
    const dispatch = useDispatch();

    return (
        <SearchInput
            id="mobileDraftAutomationsSearchInput"
            name="searchInput"
            defaultValue={queryParams.search || ''}
            onChange={(ev): void => {
                findAutomation(automations, dispatch, ev);
            }}
            onDelete={(): void => {
                findAutomation(automations, dispatch);
            }}
            placeholder={t('searchforautomations')}
            type="text"
        />
    );
};

const findAutomation = (automations, dispatch, ev?): void => {
    const search = ev ? ev.currentTarget.value : '';

    const { perPage } = automations.pagination;
    const rest = omit(automations.pagination, ['perPage', 'page']);

    debounce((): void => {
        dispatch(GetAllAutomationsStart({ ...rest, limit: perPage, name: search }));
    }, 500);
};

const RenderFilters = (): JSX.Element => {
    const { t } = useTranslation();
    const filterTabs = useMobileFilterTabs();
    const automations = useSelector(getAutomationStore);
    const dispatch = useDispatch();

    return (
        <FiltersPopover
            onApply={(obj, momentFrom, momentTo): void => {
                mobileFiltersApply(obj, momentFrom, momentTo, automations, dispatch);
            }}
            tabs={filterTabs}
            title={t('filters')}
            translations={{
                apply: t('apply'),
                close: t('close'),
            }}
        >
            <FilterIcon color={palette.gray[6]} width={20} height={20} />
        </FiltersPopover>
    );
};

const useMobileFilterTabs = (): MobileFilterTabsProps[] => {
    const { t } = useTranslation();
    const dateOptions = useDateDropdownOptions();
    const dateRangeValue = useDateDropdownValue(true);
    const query = parse(location.search);
    const startDate = query?.from?.toString();
    const endDate = query?.to?.toString();

    return [
        {
            label: t('daterange'),
            defaultValue: dateRangeValue as string,
            options: dateOptions,
            type: 'date',
            startDate,
            endDate,
        },
    ];
};

const mobileFiltersApply = (filtersObj, momentFrom, momentTo, automations, dispatch): void => {
    const dateValue = filtersObj[0];
    let from;
    let to = dayjs().utc();
    const { perPage } = automations.pagination;
    const rest = omit(automations.pagination, ['statuses', 'page', 'perPage']);

    switch (dateValue) {
        case DateDropdownOptions.Calendar:
            from = dayjs(momentFrom).add(1, 'day').utc().hour(0).minute(0).format();
            to = dayjs(momentTo).utc();
            break;
        case DateDropdownOptions.All:
            from = null;
            to = null;
            break;
        default:
            from = dayjs().subtract(dateValue, 'd').utc().hour(0).minute(0).format();
            break;
    }

    dispatch(
        GetAllAutomationsStart({
            ...rest,
            statuses: [JourneyStatus.Inactive],
            limit: perPage,
            from,
            to: to ? to.toJSON() : null,
        })
    );
};

const MobileFiltersComponent = (): JSX.Element => (
    <Header>
        <SearchContainer>
            <RenderSearchInput />
        </SearchContainer>
        <FiltersContainer>
            <RenderFilters />
        </FiltersContainer>
    </Header>
);

export default MobileFiltersComponent;
